/**
 * Demo Brand fixture — canonical logged-out / empty-state data.
 *
 * One source of truth for the example data shown to anonymous visitors
 * across every surface in the app. Every page-level `fallback-data.ts`
 * SHOULD derive its values from this file (or its helpers below) so the
 * story stays consistent: same brand, same channels, same numbers that
 * reconcile when a user flips between pages.
 *
 * The story:
 *
 *   "Acme, Inc." — a multi-channel commerce brand operating across
 *   three major US markets. ~$180k/mo paid media spend across Search,
 *   Social, TV/CTV, Programmatic, and Audio. Blended ROAS hovers
 *   around 3.2× with seasonal spikes. Onboarded 11 weeks ago, ramped
 *   to ~$45k/wk run-rate, mid-optimization on 14 active/paused
 *   campaigns.
 *
 *   The numbers tell a healthy growth story: spend up 18% MoM, ROAS up
 *   from 2.4× to 3.2× since they enabled Smart Spend, retargeting pool
 *   is rich enough to feed lookalike modeling, and Search is pulling
 *   4.1× ROAS where intent is highest.
 *
 *   Tells the prospective customer: "this is what your dashboard looks
 *   like 90 days in." Concrete enough to be believable; optimistic
 *   enough to be aspirational.
 *
 * Edits to this file ripple to every page. Treat it as the demo
 * canon — change with intent.
 */

// ---------------------------------------------------------------------------
// Brand identity
// ---------------------------------------------------------------------------

export const DEMO_BRAND = {
  name: 'Acme, Inc.',
  shortName: 'Acme',
  workspaceName: 'Acme, Inc.',
  vertical: 'Multi-channel commerce',
  domain: 'acme.example.com',
  /** Cities the brand actively services. Used for geo targeting examples. */
  markets: ['New York, NY', 'Los Angeles, CA', 'Chicago, IL'],
  /** Months since first ad live. Drives "12-week ramp" charts. */
  monthsLive: 3,
  /** Brand color used in example UI accents where a color is needed. */
  primaryColor: '#0A8A6E',
  accentColor: '#E0762D',
} as const

// ---------------------------------------------------------------------------
// Channel-level spend + performance (the spine — everything else reconciles)
// ---------------------------------------------------------------------------

export type DemoChannelKey =
  | 'search'
  | 'social'
  | 'tv'
  | 'programmatic'
  | 'audio'
  | 'retail'

/**
 * Headline channel rollup. Spend numbers ARE the monthly totals; every
 * sub/campaign breakdown across the app must sum to these values per
 * channel. Blended totals are computed automatically by helpers below.
 */
export const DEMO_CHANNELS: Record<DemoChannelKey, {
  key: DemoChannelKey
  label: string
  spendCents: number
  /** Return on ad spend, blended across the channel. */
  roas: number
  /** Active connectors / total available connectors on this channel. */
  liveConnectors: number
  totalConnectors: number
  /** Solid hex used by overview tiles and channel-detail headers. */
  hex: string
  /** 135° two-stop gradient (matches existing fallback palette). */
  gradient: string
  iconKey: DemoChannelKey
  /** Month-over-month spend delta as a fraction (0.18 = +18%). */
  spendDeltaMoM: number
  /** Month-over-month ROAS delta as absolute multiplier (+0.5 = +0.5×). */
  roasDeltaMoM: number
}> = {
  search: {
    key: 'search',
    label: 'Search',
    spendCents: 4_800_000, // $48,000
    roas: 4.1,
    liveConnectors: 2,
    totalConnectors: 6,
    hex: '#A8780C',
    gradient: 'linear-gradient(135deg, #E0A82E, #FFD95C)',
    iconKey: 'search',
    spendDeltaMoM: 0.22,
    roasDeltaMoM: 0.3,
  },
  social: {
    key: 'social',
    label: 'Social',
    spendCents: 5_200_000, // $52,000
    roas: 2.9,
    liveConnectors: 3,
    totalConnectors: 9,
    hex: '#2D6CDF',
    gradient: 'linear-gradient(135deg, #2D6CDF, #7FB0FF)',
    iconKey: 'social',
    spendDeltaMoM: 0.15,
    roasDeltaMoM: 0.2,
  },
  tv: {
    key: 'tv',
    label: 'TV / CTV',
    spendCents: 4_200_000, // $42,000
    roas: 3.1,
    liveConnectors: 4,
    totalConnectors: 16,
    hex: '#7A5BD6',
    gradient: 'linear-gradient(135deg, #7A5BD6, #B9A6FF)',
    iconKey: 'tv',
    spendDeltaMoM: 0.18,
    roasDeltaMoM: 0.4,
  },
  programmatic: {
    key: 'programmatic',
    label: 'Programmatic',
    spendCents: 2_400_000, // $24,000
    roas: 2.2,
    liveConnectors: 2,
    totalConnectors: 12,
    hex: '#0A8A6E',
    gradient: 'linear-gradient(135deg, #0A8A6E, #2BC39A)',
    iconKey: 'programmatic',
    spendDeltaMoM: -0.08,
    roasDeltaMoM: -0.1,
  },
  audio: {
    key: 'audio',
    label: 'Audio',
    spendCents: 1_200_000, // $12,000
    roas: 2.6,
    liveConnectors: 1,
    totalConnectors: 5,
    hex: '#C04E2F',
    gradient: 'linear-gradient(135deg, #E0762D, #FFB36B)',
    iconKey: 'audio',
    spendDeltaMoM: 0.42,
    roasDeltaMoM: 0.1,
  },
  retail: {
    key: 'retail',
    label: 'Retail',
    spendCents: 0,
    roas: 0,
    liveConnectors: 0,
    totalConnectors: 4,
    hex: '#9D4EE0',
    gradient: 'linear-gradient(135deg, #9D4EE0, #C589FF)',
    iconKey: 'retail',
    spendDeltaMoM: 0,
    roasDeltaMoM: 0,
  },
}

// ---------------------------------------------------------------------------
// Subchannels per channel (sum to channel spend / weight ROAS to channel ROAS)
// ---------------------------------------------------------------------------

export interface DemoSubchannel {
  key: string
  name: string
  channel: DemoChannelKey
  spendCents: number
  roas: number
  /** ISO date of last successful sync from this connector. */
  lastSyncIso: string
  status: 'connected' | 'connecting' | 'paused' | 'attention'
}

const RECENT_SYNC = (minutesAgo: number) =>
  new Date(Date.now() - minutesAgo * 60_000).toISOString()

export const DEMO_SUBCHANNELS: DemoSubchannel[] = [
  // Search ($48k @ 4.1×)
  { key: 'google_ads', name: 'Google Ads', channel: 'search', spendCents: 3_700_000, roas: 4.2, lastSyncIso: RECENT_SYNC(15), status: 'connected' },
  { key: 'microsoft_ads', name: 'Microsoft Ads', channel: 'search', spendCents: 1_100_000, roas: 3.7, lastSyncIso: RECENT_SYNC(22), status: 'connected' },

  // Social ($52k @ 2.9×)
  { key: 'meta', name: 'Meta', channel: 'social', spendCents: 3_100_000, roas: 3.1, lastSyncIso: RECENT_SYNC(8), status: 'connected' },
  { key: 'tiktok', name: 'TikTok', channel: 'social', spendCents: 1_300_000, roas: 2.6, lastSyncIso: RECENT_SYNC(45), status: 'connected' },
  { key: 'pinterest', name: 'Pinterest', channel: 'social', spendCents: 800_000, roas: 2.4, lastSyncIso: RECENT_SYNC(120), status: 'connected' },

  // TV/CTV ($42k @ 3.1×)
  { key: 'roku', name: 'Roku', channel: 'tv', spendCents: 1_500_000, roas: 3.0, lastSyncIso: RECENT_SYNC(180), status: 'connected' },
  { key: 'peacock', name: 'Peacock', channel: 'tv', spendCents: 900_000, roas: 3.3, lastSyncIso: RECENT_SYNC(200), status: 'connected' },
  { key: 'hulu', name: 'Hulu', channel: 'tv', spendCents: 1_000_000, roas: 3.1, lastSyncIso: RECENT_SYNC(240), status: 'connected' },
  { key: 'nbcuniversal', name: 'NBC Universal', channel: 'tv', spendCents: 800_000, roas: 2.9, lastSyncIso: RECENT_SYNC(300), status: 'connected' },

  // Programmatic ($24k @ 2.2×)
  { key: 'ttd', name: 'The Trade Desk', channel: 'programmatic', spendCents: 1_600_000, roas: 2.3, lastSyncIso: RECENT_SYNC(60), status: 'connected' },
  { key: 'dv360', name: 'DV360', channel: 'programmatic', spendCents: 800_000, roas: 2.0, lastSyncIso: RECENT_SYNC(75), status: 'attention' },

  // Audio ($12k @ 2.6×)
  { key: 'spotify', name: 'Spotify', channel: 'audio', spendCents: 1_200_000, roas: 2.6, lastSyncIso: RECENT_SYNC(90), status: 'connected' },
]

// ---------------------------------------------------------------------------
// Campaigns (sum to channel spend; reconciles with /campaigns + /channels)
// ---------------------------------------------------------------------------

export type DemoCampaignStatus = 'Active' | 'Paused' | 'Draft'
export type DemoCampaignGoal = 'Conversions' | 'Traffic' | 'Awareness'

export interface DemoCampaign {
  id: string
  name: string
  channel: DemoChannelKey
  subchannel: string // matches DEMO_SUBCHANNELS.key
  status: DemoCampaignStatus
  goal: DemoCampaignGoal
  /** Monthly impressions in the demo data. */
  impressions: number
  spendCents: number
  roas: number
  /** ISO date launched. */
  launchedIso: string
}

const DAYS_AGO = (n: number): string => {
  const d = new Date()
  d.setDate(d.getDate() - n)
  return d.toISOString().slice(0, 10)
}

export const DEMO_CAMPAIGNS: DemoCampaign[] = [
  // Search — $48k across 3 campaigns
  { id: 'demo-camp-1', name: 'Brand Search — Always-On', channel: 'search', subchannel: 'google_ads', status: 'Active', goal: 'Conversions', impressions: 1_240_000, spendCents: 2_650_000, roas: 4.3, launchedIso: DAYS_AGO(78) },
  { id: 'demo-camp-2', name: 'Category Keywords — Google', channel: 'search', subchannel: 'google_ads', status: 'Active', goal: 'Traffic', impressions: 640_000, spendCents: 1_050_000, roas: 3.9, launchedIso: DAYS_AGO(72) },
  { id: 'demo-camp-3', name: 'Competitor Conquest — Bing', channel: 'search', subchannel: 'microsoft_ads', status: 'Active', goal: 'Conversions', impressions: 380_000, spendCents: 1_100_000, roas: 4.1, launchedIso: DAYS_AGO(55) },

  // Social — $52k across 4 campaigns
  { id: 'demo-camp-4', name: 'Always-On Retargeting', channel: 'social', subchannel: 'meta', status: 'Active', goal: 'Conversions', impressions: 980_000, spendCents: 1_840_000, roas: 3.4, launchedIso: DAYS_AGO(81) },
  { id: 'demo-camp-5', name: 'Spring Promo — Lookalikes', channel: 'social', subchannel: 'meta', status: 'Active', goal: 'Conversions', impressions: 1_460_000, spendCents: 1_260_000, roas: 2.6, launchedIso: DAYS_AGO(34) },
  { id: 'demo-camp-6', name: 'Brand Awareness — TikTok', channel: 'social', subchannel: 'tiktok', status: 'Paused', goal: 'Awareness', impressions: 2_100_000, spendCents: 1_300_000, roas: 1.9, launchedIso: DAYS_AGO(46) },
  { id: 'demo-camp-7', name: 'Pinterest — Product Discovery', channel: 'social', subchannel: 'pinterest', status: 'Active', goal: 'Traffic', impressions: 540_000, spendCents: 800_000, roas: 2.4, launchedIso: DAYS_AGO(28) },

  // TV/CTV — $42k across 4 campaigns
  { id: 'demo-camp-8', name: 'Spring Launch — Roku CTV', channel: 'tv', subchannel: 'roku', status: 'Active', goal: 'Conversions', impressions: 820_000, spendCents: 1_500_000, roas: 3.0, launchedIso: DAYS_AGO(40) },
  { id: 'demo-camp-9', name: 'Peacock Sports Sponsorship', channel: 'tv', subchannel: 'peacock', status: 'Active', goal: 'Awareness', impressions: 1_100_000, spendCents: 900_000, roas: 3.3, launchedIso: DAYS_AGO(63) },
  { id: 'demo-camp-10', name: 'Hulu Primetime Block', channel: 'tv', subchannel: 'hulu', status: 'Active', goal: 'Conversions', impressions: 950_000, spendCents: 1_000_000, roas: 3.1, launchedIso: DAYS_AGO(52) },
  { id: 'demo-camp-11', name: 'NBC News Local — Test', channel: 'tv', subchannel: 'nbcuniversal', status: 'Active', goal: 'Awareness', impressions: 720_000, spendCents: 800_000, roas: 2.9, launchedIso: DAYS_AGO(22) },

  // Programmatic — $24k across 2 campaigns
  { id: 'demo-camp-12', name: 'Lookalike Prospecting — DSP', channel: 'programmatic', subchannel: 'ttd', status: 'Active', goal: 'Conversions', impressions: 1_540_000, spendCents: 1_600_000, roas: 2.3, launchedIso: DAYS_AGO(67) },
  { id: 'demo-camp-13', name: 'Cart Abandoners — DSP Retargeting', channel: 'programmatic', subchannel: 'dv360', status: 'Paused', goal: 'Traffic', impressions: 380_000, spendCents: 800_000, roas: 2.0, launchedIso: DAYS_AGO(89) },

  // Audio — $12k across 1 campaign
  { id: 'demo-camp-14', name: 'Spotify Commute Block — NYC', channel: 'audio', subchannel: 'spotify', status: 'Active', goal: 'Awareness', impressions: 680_000, spendCents: 1_200_000, roas: 2.6, launchedIso: DAYS_AGO(38) },
]

// ---------------------------------------------------------------------------
// Customers / audiences (drives /customers, /properties)
// ---------------------------------------------------------------------------

export interface DemoCustomerSegment {
  key: string
  name: string
  description: string
  count: number
  /** Used as the "what this segment is for" badge. */
  meta: string
  iconKey: 'users' | 'cart-off' | 'check' | 'topology' | 'form' | 'gauge'
  gradient: string
}

export const DEMO_CUSTOMER_SEGMENTS: DemoCustomerSegment[] = [
  {
    key: 'all-visitors',
    name: 'All site visitors',
    description: 'Everyone who hit acme.example.com in the last 30 days.',
    count: 48_200,
    meta: 'Updated daily · base segment',
    iconKey: 'users',
    gradient: 'linear-gradient(135deg, #2D6CDF, #7FB0FF)',
  },
  {
    key: 'cart-abandoners',
    name: 'Cart abandoners',
    description: 'Added to cart but didn\'t check out. Prime retargeting pool.',
    count: 6_400,
    meta: 'Retargeting pool · 30-day window',
    iconKey: 'cart-off',
    gradient: 'linear-gradient(135deg, #7A5BD6, #B9A6FF)',
  },
  {
    key: 'past-converters',
    name: 'Past purchasers',
    description: 'Purchased in the last 12 months. Used for suppression + lookalike seeds.',
    count: 1_240,
    meta: 'Suppression + lookalike seed',
    iconKey: 'check',
    gradient: 'linear-gradient(135deg, #0A8A6E, #2BC39A)',
  },
  {
    key: 'lookalike-customers',
    name: 'Lookalike — high-intent shoppers',
    description: 'Modeled from past purchasers. Seeded against Meta + Google audiences.',
    count: 2_100_000,
    meta: 'Modeled from past purchasers',
    iconKey: 'topology',
    gradient: 'linear-gradient(135deg, #E0762D, #FFB36B)',
  },
  {
    key: 'high-value',
    name: 'High-LTV customers',
    description: 'Top 10% of past purchasers by lifetime value. Used for VIP campaigns + lookalike seeding.',
    count: 124,
    meta: 'LTV-tiered · refreshed weekly',
    iconKey: 'gauge',
    gradient: 'linear-gradient(135deg, #A8780C, #E0A82E)',
  },
]

// ---------------------------------------------------------------------------
// Tracked properties / pixel events (drives /properties)
// ---------------------------------------------------------------------------

export interface DemoTrackedProperty {
  key: string
  name: string
  path: string
  /** Daily event count (last 24h). */
  events: number
  /** Whether this is the primary conversion. */
  primary: boolean
  gradient: string
  iconKey: 'form' | 'check' | 'cart' | 'play'
}

export const DEMO_TRACKED_PROPERTIES: DemoTrackedProperty[] = [
  { key: 'purchase', name: 'Purchase', path: '/checkout/success', events: 142, primary: true, iconKey: 'check', gradient: 'linear-gradient(135deg, #0A8A6E, #2BC39A)' },
  { key: 'add-to-cart', name: 'Add to cart', path: '/api/cart/add', events: 1_840, primary: true, iconKey: 'cart', gradient: 'linear-gradient(135deg, #A8780C, #E0A82E)' },
  { key: 'checkout-start', name: 'Begin checkout', path: '/checkout', events: 420, primary: false, iconKey: 'form', gradient: 'linear-gradient(135deg, #2D6CDF, #7FB0FF)' },
  { key: 'product-view', name: 'Product detail view', path: '/products/*', events: 12_640, primary: false, iconKey: 'play', gradient: 'linear-gradient(135deg, #7A5BD6, #B9A6FF)' },
]

// ---------------------------------------------------------------------------
// Creatives (drives /creatives + campaign detail)
// ---------------------------------------------------------------------------

export interface DemoCreative {
  id: string
  name: string
  channel: DemoChannelKey
  format: 'Video' | 'Image' | 'Text' | 'Audio'
  duration?: string
  impressions: number
  roas: number
  status: 'Active' | 'Draft' | 'Paused'
}

export const DEMO_CREATIVES: DemoCreative[] = [
  { id: 'demo-creative-1', name: 'Spring Launch — Hero 30s', channel: 'tv', format: 'Video', duration: '0:30', impressions: 1_240_000, roas: 4.3, status: 'Active' },
  { id: 'demo-creative-2', name: 'Product Showcase — 15s Cut', channel: 'tv', format: 'Video', duration: '0:15', impressions: 820_000, roas: 3.1, status: 'Active' },
  { id: 'demo-creative-3', name: 'Peacock Sponsor Bumper :06', channel: 'tv', format: 'Video', duration: '0:06', impressions: 1_100_000, roas: 3.3, status: 'Active' },
  { id: 'demo-creative-4', name: 'Spring Promo — Meta 30s', channel: 'social', format: 'Video', duration: '0:30', impressions: 1_460_000, roas: 2.6, status: 'Active' },
  { id: 'demo-creative-5', name: 'Brand Hero — Square', channel: 'social', format: 'Image', impressions: 980_000, roas: 3.4, status: 'Active' },
  { id: 'demo-creative-6', name: 'Retarget Carousel — 6 panels', channel: 'social', format: 'Image', impressions: 540_000, roas: 3.0, status: 'Active' },
  { id: 'demo-creative-7', name: 'Search RSA Pack — Brand', channel: 'search', format: 'Text', impressions: 640_000, roas: 3.9, status: 'Active' },
  { id: 'demo-creative-8', name: 'Search RSA Pack — Category', channel: 'search', format: 'Text', impressions: 1_240_000, roas: 4.3, status: 'Active' },
  { id: 'demo-creative-9', name: 'Brand Banner 300×250', channel: 'programmatic', format: 'Image', impressions: 1_540_000, roas: 2.3, status: 'Active' },
  { id: 'demo-creative-10', name: 'Commute 30s — Spotify', channel: 'audio', format: 'Audio', duration: '0:30', impressions: 680_000, roas: 2.6, status: 'Active' },
  { id: 'demo-creative-11', name: 'Spring Launch — Roku CTV', channel: 'tv', format: 'Video', duration: '0:30', impressions: 820_000, roas: 3.0, status: 'Active' },
  { id: 'demo-creative-12', name: 'Seasonal Teaser — DSP', channel: 'programmatic', format: 'Video', duration: '0:15', impressions: 380_000, roas: 2.0, status: 'Draft' },
]

// ---------------------------------------------------------------------------
// Budget / wallet
// ---------------------------------------------------------------------------

export const DEMO_BUDGET = {
  /** Monthly cap. */
  monthlyCapCents: 20_000_000, // $200,000
  /** Spent so far this month. */
  monthToDateCents: 17_800_000, // $178,000 (matches sum of channel spend)
  /** Days remaining in current billing cycle. */
  daysRemaining: 7,
  /** Auto-reload threshold + amount. */
  autoReloadEnabled: true,
  autoReloadThresholdCents: 2_500_000, // $25k
  autoReloadAmountCents: 5_000_000, // $50k
} as const

export const DEMO_WALLET = {
  balanceCents: 4_200_000, // $42,000 current balance
  /** 7-day avg daily burn. */
  avgDailyBurnCents: 590_000, // $5,900/day
  /** Days of runway at current burn. */
  runwayDays: 7.1,
  lastReloadCents: 5_000_000,
  lastReloadIso: DAYS_AGO(6) + 'T14:32:00.000Z',
} as const

// ---------------------------------------------------------------------------
// Commands + scheduled (drives /commands, /scheduled, /marketplace)
// ---------------------------------------------------------------------------

export interface DemoCommand {
  id: string
  name: string
  description: string
  category: 'optimization' | 'creative' | 'cleanup' | 'intelligence' | 'reporting'
  channels: DemoChannelKey[]
  enabled: boolean
  /** Number of times run in the last 7 days. */
  runsLast7d: number
}

export const DEMO_COMMANDS: DemoCommand[] = [
  { id: 'demo-cmd-rebalance', name: 'Rebalance Budgets', description: 'Shift spend from low-ROAS subchannels to high-ROAS subchannels every Monday.', category: 'optimization', channels: ['search', 'social', 'tv', 'programmatic'], enabled: true, runsLast7d: 7 },
  { id: 'demo-cmd-pause-weak', name: 'Pause Weak Performers', description: 'Pause any subchannel below the ROAS floor for 7 days running.', category: 'optimization', channels: ['search', 'social', 'tv', 'programmatic'], enabled: true, runsLast7d: 7 },
  { id: 'demo-cmd-negative-keyword', name: 'Negative Keyword Mining', description: 'Pull search query reports daily and add wasteful keywords as negatives.', category: 'intelligence', channels: ['search'], enabled: true, runsLast7d: 7 },
  { id: 'demo-cmd-fraud-sweep', name: 'Fraud Sweep', description: 'Scan placements for invalid traffic, bots, and click fraud. Pause offenders.', category: 'cleanup', channels: ['programmatic'], enabled: true, runsLast7d: 7 },
  { id: 'demo-cmd-refresh-creative', name: 'Refresh Creative', description: 'Generate new ad variants when a creative\'s ROAS slips >15% week-over-week.', category: 'creative', channels: ['social', 'tv', 'programmatic'], enabled: true, runsLast7d: 4 },
  { id: 'demo-cmd-weekly-digest', name: 'Weekly Digest', description: 'Email a Monday-morning summary of what happened and what changed.', category: 'reporting', channels: ['search', 'social', 'tv', 'programmatic', 'audio'], enabled: true, runsLast7d: 1 },
]

export interface DemoSchedule {
  id: string
  commandName: string
  cadence: string
  nextRunIso: string
  lastRunIso: string
  status: 'healthy' | 'degraded' | 'failing'
}

export const DEMO_SCHEDULES: DemoSchedule[] = [
  { id: 'demo-sched-1', commandName: 'Rebalance Budgets', cadence: 'Weekly · Monday 9:00 AM', nextRunIso: new Date(Date.now() + 2 * 86400_000).toISOString(), lastRunIso: new Date(Date.now() - 5 * 86400_000).toISOString(), status: 'healthy' },
  { id: 'demo-sched-2', commandName: 'Pause Weak Performers', cadence: 'Daily · 6:00 AM', nextRunIso: new Date(Date.now() + 14 * 3600_000).toISOString(), lastRunIso: new Date(Date.now() - 10 * 3600_000).toISOString(), status: 'healthy' },
  { id: 'demo-sched-3', commandName: 'Negative Keyword Mining', cadence: 'Daily · 7:00 AM', nextRunIso: new Date(Date.now() + 15 * 3600_000).toISOString(), lastRunIso: new Date(Date.now() - 9 * 3600_000).toISOString(), status: 'healthy' },
  { id: 'demo-sched-4', commandName: 'Fraud Sweep', cadence: 'Daily · 4:00 AM', nextRunIso: new Date(Date.now() + 12 * 3600_000).toISOString(), lastRunIso: new Date(Date.now() - 12 * 3600_000).toISOString(), status: 'healthy' },
  { id: 'demo-sched-5', commandName: 'Refresh Creative', cadence: 'Triggered · ROAS drop ≥15%', nextRunIso: '', lastRunIso: new Date(Date.now() - 36 * 3600_000).toISOString(), status: 'healthy' },
]

// ---------------------------------------------------------------------------
// AI agents (drives sidebar agents view, /subagents)
// ---------------------------------------------------------------------------

export interface DemoAgent {
  key: string
  name: string
  channel: 'search' | 'social' | 'tv' | 'programmatic' | 'audio' | 'creative' | 'workspace'
  role: string
  actsLabel: string
  lastRun: string
  enabled: boolean
}

export const DEMO_AGENTS: DemoAgent[] = [
  { key: 'search-agent', name: 'Search Agent', channel: 'search', role: 'Manages keywords, bids, and negatives across Google + Bing.', actsLabel: '18 actions this week', lastRun: '2h ago', enabled: true },
  { key: 'social-agent', name: 'Social Agent', channel: 'social', role: 'Tunes audiences, creative rotation, and pacing on Meta + TikTok + Pinterest.', actsLabel: '24 actions this week', lastRun: '40m ago', enabled: true },
  { key: 'tv-agent', name: 'TV / CTV Agent', channel: 'tv', role: 'Allocates streaming spend across Roku, Peacock, Hulu, NBC with frequency caps.', actsLabel: '11 actions this week', lastRun: '1h ago', enabled: true },
  { key: 'programmatic-agent', name: 'Programmatic Agent', channel: 'programmatic', role: 'Optimizes DSP bids, supply paths, and fraud filters on TTD + DV360.', actsLabel: '31 actions this week', lastRun: '15m ago', enabled: true },
  { key: 'audio-agent', name: 'Audio Agent', channel: 'audio', role: 'Daypart targeting and creative cadence for Spotify commute blocks.', actsLabel: '6 actions this week', lastRun: '3h ago', enabled: true },
  { key: 'creative-agent', name: 'Creative Agent', channel: 'creative', role: 'Generates ad variants and refreshes creative when ROAS slips.', actsLabel: '4 assets this week', lastRun: '5h ago', enabled: true },
  { key: 'budget-agent', name: 'Budget Agent', channel: 'workspace', role: 'Watches monthly pacing, projects month-end spend, alerts on overrun risk.', actsLabel: 'Watching 5 channels', lastRun: '1m ago', enabled: true },
]

// ---------------------------------------------------------------------------
// Alerts (drives /alerts logged-out — coherent with the demo narrative)
// ---------------------------------------------------------------------------

export interface DemoAlertRow {
  id: string
  channel: 'search' | 'social' | 'tv' | 'programmatic' | 'audio' | 'workspace'
  severity: 'crit' | 'warn' | 'done' | 'info'
  iconKey: string
  title: string
  detail: string
  time: string
  action: string
  unread: boolean
}

export const DEMO_ALERTS: DemoAlertRow[] = [
  { id: 'demo-alert-1', channel: 'search', severity: 'done', iconKey: 'filter', title: 'Negative Keyword Mining complete', detail: 'Added 34 negatives across Google + Bing — projected −$310/mo in wasted spend.', time: '2h ago', action: 'View report', unread: true },
  { id: 'demo-alert-2', channel: 'search', severity: 'info', iconKey: 'trending-up', title: 'Quality Score improving', detail: 'Google Ads average Quality Score up to 8.4 after the RSA refresh.', time: '1d ago', action: '', unread: false },
  { id: 'demo-alert-3', channel: 'social', severity: 'crit', iconKey: 'key', title: 'Pinterest token expires in 3 days', detail: 'Reauthorize the Pinterest connector to avoid a pause in delivery.', time: '3h ago', action: 'Reconnect', unread: true },
  { id: 'demo-alert-4', channel: 'social', severity: 'warn', iconKey: 'trending-down', title: 'Brand Awareness — TikTok below ROAS floor', detail: '1.9× for 7 days, under your 2.4× floor. Auto-paused by Pause Weak Performers.', time: '5h ago', action: 'Review', unread: true },
  { id: 'demo-alert-5', channel: 'tv', severity: 'done', iconKey: 'activity', title: 'Spring Launch — Roku approved', detail: 'Creative review passed. Live in 24h on Roku CTV.', time: '6h ago', action: '', unread: false },
  { id: 'demo-alert-6', channel: 'programmatic', severity: 'warn', iconKey: 'gauge', title: 'Cart Abandoners — DSP pacing slow', detail: 'Tracking to spend $640 under monthly pace. Rebalance Budgets will adjust Monday.', time: '8h ago', action: 'Adjust', unread: false },
  { id: 'demo-alert-7', channel: 'workspace', severity: 'info', iconKey: 'wallet', title: 'Wallet auto-reload triggered', detail: '$5,000 added when balance dropped below $2,500. Runway ~7 days.', time: '6d ago', action: '', unread: false },
  { id: 'demo-alert-8', channel: 'workspace', severity: 'done', iconKey: 'shield', title: 'Fraud Sweep clean', detail: 'Scanned 1.6M impressions across DSP supply paths. 0 invalid placements blocked today.', time: '12h ago', action: '', unread: false },
]

// ---------------------------------------------------------------------------
// Time series — 12-week ramp + 90-day daily for charts
// ---------------------------------------------------------------------------

/**
 * Generate a 12-week ramp series for spend OR ROAS. Used by charts that
 * want to show "we've grown from week 1 to week 12." Each week is a
 * (week_index, value) tuple where week 0 is 11 weeks ago.
 *
 * Ramp curve: slow start → acceleration around week 5 → plateau around
 * week 9 with mild seasonal noise. Tells "we onboarded, smart spend
 * kicked in, then we scaled." Same shape used everywhere for consistency.
 */
export function demoWeeklySpendCents(weeks: number = 12): Array<{ week: number; spendCents: number }> {
  const total = totalMonthlySpendCents()
  const weeklyTarget = total / 4 // monthly ÷ 4 weeks
  return Array.from({ length: weeks }, (_, i) => {
    // S-curve: 0.25× target at week 0 → 1.05× target at week 11
    const t = i / (weeks - 1)
    const sigmoid = 1 / (1 + Math.exp(-6 * (t - 0.5)))
    const factor = 0.25 + sigmoid * 0.85
    const noise = 1 + Math.sin(i * 1.4) * 0.05
    return { week: i, spendCents: Math.round(weeklyTarget * factor * noise) }
  })
}

export function demoWeeklyRoas(weeks: number = 12): Array<{ week: number; roas: number }> {
  // ROAS climbs from 2.4× to 3.2× over the ramp, with mild noise.
  return Array.from({ length: weeks }, (_, i) => {
    const t = i / (weeks - 1)
    const value = 2.4 + t * 0.8 + Math.cos(i * 0.9) * 0.08
    return { week: i, roas: Math.round(value * 10) / 10 }
  })
}

/**
 * Daily performance row — matches the shape `CampaignPerformanceChart`
 * expects (PerformanceRow). One row per (campaign, day) over the lookback.
 */
export interface DemoDailyPerfRow {
  campaign_id: string
  date: string // 'YYYY-MM-DD'
  impressions: number
  clicks: number
  spend: number // cents
  cpm: number // cents
  ctr: number // 0-1
  video_completion_rate: number
  video_q1: number
  video_q2: number
  video_q3: number
  video_q4: number
  viewability: number
  vast_errors: number
}

/**
 * Generate a 90-day daily performance series for one campaign. Total spend
 * over the window approximates the campaign's monthly run-rate times the
 * months-live. Pacing follows a slow ramp → steady curve with weekly
 * seasonality (weekends spend less on B2C-ish channels).
 *
 * Per-day spend is derived from the campaign's monthly `spendCents` so the
 * daily totals roll up to a multiple of the headline. Impressions/clicks/CPM
 * reconcile via spendCents = impressions * cpmCents / 1000.
 */
export function demoDailyPerfForCampaign(
  campaignId: string,
  days: number = 90,
): DemoDailyPerfRow[] {
  const campaign = DEMO_CAMPAIGNS.find((c) => c.id === campaignId)
  if (!campaign) return []

  // Daily target spend = (monthly spend) / 30, scaled by ramp curve so the
  // 90-day total comes out to roughly 3× monthly (~3 months of data).
  const dailyTargetCents = campaign.spendCents / 30

  // Click-through rate by channel — realistic ranges.
  const ctrByChannel: Record<DemoChannelKey, number> = {
    search: 0.045,
    social: 0.012,
    tv: 0,
    programmatic: 0.0035,
    audio: 0,
    retail: 0.02,
  }
  const ctr = ctrByChannel[campaign.channel] ?? 0.01

  // CPM by channel — realistic ranges.
  const cpmCentsByChannel: Record<DemoChannelKey, number> = {
    search: 700, // $7
    social: 1100, // $11
    tv: 3200, // $32
    programmatic: 850, // $8.50
    audio: 1500, // $15
    retail: 1800,
  }
  const baseCpmCents = cpmCentsByChannel[campaign.channel] ?? 1000

  // Video metrics by channel — TV/Programmatic have richer video data.
  const videoChannel = campaign.channel === 'tv' || campaign.channel === 'programmatic' || campaign.channel === 'audio'

  return Array.from({ length: days }, (_, i) => {
    const daysAgo = days - 1 - i
    const d = new Date()
    d.setDate(d.getDate() - daysAgo)
    const dateIso = d.toISOString().slice(0, 10)
    const dayOfWeek = d.getDay() // 0 = Sun, 6 = Sat

    // Ramp curve: 0.6× at start → 1.1× at end with smooth S-curve.
    const t = i / (days - 1)
    const sigmoid = 1 / (1 + Math.exp(-5 * (t - 0.5)))
    const rampFactor = 0.6 + sigmoid * 0.55

    // Weekly seasonality: weekends -10% spend on social, -15% on programmatic.
    let weekendFactor = 1
    if (dayOfWeek === 0 || dayOfWeek === 6) {
      if (campaign.channel === 'social') weekendFactor = 0.9
      else if (campaign.channel === 'programmatic') weekendFactor = 0.85
      else if (campaign.channel === 'search') weekendFactor = 0.95
    }

    // Small daily noise.
    const noise = 1 + Math.sin(i * 0.91) * 0.06 + Math.cos(i * 1.7) * 0.04

    const spendCents = Math.round(dailyTargetCents * rampFactor * weekendFactor * noise)
    const cpmCents = Math.round(baseCpmCents * (0.95 + Math.sin(i * 0.5) * 0.08))
    const impressions = cpmCents > 0 ? Math.round((spendCents * 1000) / cpmCents) : 0
    const clicks = Math.round(impressions * ctr)

    return {
      campaign_id: campaignId,
      date: dateIso,
      impressions,
      clicks,
      spend: spendCents,
      cpm: cpmCents,
      ctr,
      video_completion_rate: videoChannel ? 0.78 : 0,
      video_q1: videoChannel ? Math.round(impressions * 0.94) : 0,
      video_q2: videoChannel ? Math.round(impressions * 0.87) : 0,
      video_q3: videoChannel ? Math.round(impressions * 0.82) : 0,
      video_q4: videoChannel ? Math.round(impressions * 0.78) : 0,
      viewability: campaign.channel === 'programmatic' ? 0.71 : campaign.channel === 'tv' ? 0.95 : 0.82,
      vast_errors: videoChannel ? Math.round(impressions * 0.008) : 0,
    }
  })
}

/**
 * Aggregate daily performance series for ALL active demo campaigns.
 * Used by visualizer / overview charts that want a single full-portfolio series.
 */
export function demoDailyPerfAllCampaigns(days: number = 90): DemoDailyPerfRow[] {
  return DEMO_CAMPAIGNS.flatMap((c) => demoDailyPerfForCampaign(c.id, days))
}

/**
 * Channel-detail compareData shape. Used by ChannelDetailPage's LineChart.
 * Returns N daily points with an `offset` (0..N-1), spend_cents, and a
 * prior-period comparison value. The prior period is 0.78× the current
 * period for a "+22% MoM" growth story.
 */
export interface DemoChannelCompareSeries {
  current: {
    from: string // YYYY-MM-DD
    to: string
    series: Array<{ offset: number; spend_cents: number; current_date: string }>
  }
  prior: {
    from: string
    to: string
    series: Array<{ offset: number; spend_cents: number; prior_date: string }>
  }
}

export function demoChannelCompareSeries(
  channel: DemoChannelKey,
  days: number = 30,
): DemoChannelCompareSeries {
  const channelInfo = DEMO_CHANNELS[channel]
  const monthlySpend = channelInfo.spendCents
  const dailyTarget = monthlySpend / 30

  const today = new Date()
  const currentEnd = new Date(today)
  const currentStart = new Date(today)
  currentStart.setDate(currentStart.getDate() - days + 1)
  const priorEnd = new Date(currentStart)
  priorEnd.setDate(priorEnd.getDate() - 1)
  const priorStart = new Date(priorEnd)
  priorStart.setDate(priorStart.getDate() - days + 1)

  const fmt = (d: Date) => d.toISOString().slice(0, 10)

  const current = Array.from({ length: days }, (_, i) => {
    const d = new Date(currentStart)
    d.setDate(d.getDate() + i)
    const t = i / (days - 1)
    const sigmoid = 1 / (1 + Math.exp(-4 * (t - 0.5)))
    const factor = 0.85 + sigmoid * 0.35
    const noise = 1 + Math.sin(i * 0.8) * 0.07
    const dayOfWeek = d.getDay()
    const weekendFactor = (dayOfWeek === 0 || dayOfWeek === 6) ? 0.92 : 1
    return {
      offset: i,
      spend_cents: Math.round(dailyTarget * factor * noise * weekendFactor),
      current_date: fmt(d),
    }
  })

  const prior = Array.from({ length: days }, (_, i) => {
    const d = new Date(priorStart)
    d.setDate(d.getDate() + i)
    const t = i / (days - 1)
    const factor = 0.7 + t * 0.2 // flatter, lower
    const noise = 1 + Math.cos(i * 0.7) * 0.06
    const dayOfWeek = d.getDay()
    const weekendFactor = (dayOfWeek === 0 || dayOfWeek === 6) ? 0.9 : 1
    return {
      offset: i,
      spend_cents: Math.round(dailyTarget * factor * noise * weekendFactor),
      prior_date: fmt(d),
    }
  })

  return {
    current: { from: fmt(currentStart), to: fmt(currentEnd), series: current },
    prior: { from: fmt(priorStart), to: fmt(priorEnd), series: prior },
  }
}

/**
 * Overview chart series — last 30 days spend by channel, all channels.
 * Returns one entry per channel with a 30-point daily spend series.
 */
export function demoOverviewChannelSeries(days: number = 30): Array<{
  channel: DemoChannelKey
  label: string
  hex: string
  series: Array<{ offset: number; spend_cents: number; date: string }>
}> {
  return Object.values(DEMO_CHANNELS)
    .filter((c) => c.spendCents > 0)
    .map((channel) => {
      const compare = demoChannelCompareSeries(channel.key, days)
      return {
        channel: channel.key,
        label: channel.label,
        hex: channel.hex,
        series: compare.current.series.map(({ offset, spend_cents, current_date }) => ({
          offset,
          spend_cents,
          date: current_date,
        })),
      }
    })
}

// ---------------------------------------------------------------------------
// Helpers — computed rollups
// ---------------------------------------------------------------------------

export function totalMonthlySpendCents(): number {
  return Object.values(DEMO_CHANNELS).reduce((sum, c) => sum + c.spendCents, 0)
}

export function blendedRoas(): number {
  const totals = Object.values(DEMO_CHANNELS).reduce(
    (acc, c) => {
      acc.spend += c.spendCents
      acc.revenue += c.spendCents * c.roas
      return acc
    },
    { spend: 0, revenue: 0 },
  )
  if (totals.spend === 0) return 0
  return Math.round((totals.revenue / totals.spend) * 10) / 10
}

export function campaignsByChannel(channel: DemoChannelKey): DemoCampaign[] {
  return DEMO_CAMPAIGNS.filter(c => c.channel === channel)
}

export function subchannelsByChannel(channel: DemoChannelKey): DemoSubchannel[] {
  return DEMO_SUBCHANNELS.filter(s => s.channel === channel)
}

export function creativesByChannel(channel: DemoChannelKey): DemoCreative[] {
  return DEMO_CREATIVES.filter(c => c.channel === channel)
}

// ---------------------------------------------------------------------------
// Display helpers (consistent formatting across all surfaces)
// ---------------------------------------------------------------------------

export function formatCents(cents: number, opts: { withCents?: boolean } = {}): string {
  const dollars = cents / 100
  const fmt = new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: opts.withCents ? 2 : 0,
    maximumFractionDigits: opts.withCents ? 2 : 0,
  })
  return fmt.format(dollars)
}

export function formatCompactCents(cents: number): string {
  const dollars = cents / 100
  if (dollars >= 1_000_000) return `$${(dollars / 1_000_000).toFixed(1)}M`
  if (dollars >= 1_000) return `$${Math.round(dollars / 1_000)}k`
  return `$${Math.round(dollars)}`
}

export function formatCompactNumber(n: number): string {
  if (n >= 1_000_000) return `${(n / 1_000_000).toFixed(1)}M`
  if (n >= 1_000) return `${(n / 1_000).toFixed(n >= 100_000 ? 0 : 1)}K`
  return `${n}`
}

export function formatRoas(roas: number): string {
  if (!Number.isFinite(roas) || roas <= 0) return '—'
  return `${roas.toFixed(1)}×`
}
